import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { isNone } from '@ember/utils'
import type Router from 'teacher-ui-client/router'
import type SessionService from 'teacher-ui-client/services/session'
import type SubscriptionTypeRoute from 'teacher-ui-client/subscription-type/route'
import type Owner from '@ember/owner'
import type Transition from '@ember/routing/transition'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { IntlService } from 'ember-intl'

export default class SubscriptionTypeController extends Controller<ModelFor<SubscriptionTypeRoute>> {
  @service router!: Router

  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  @service intl!: IntlService

  get hasUiScope() {
    return this.router.currentRouteName?.includes('ui-scope')
  }

  // Want to introduce the idea of hiding these counts, for now only on the new 'teacher-home' / teacher insights page. This can be expanded into a list later if needed.
  get disableCounts() {
    return this.router.currentRouteName?.includes('teacher-home')
  }

  get currentTeacher() {
    return this.session.currentTeacher
  }

  get isSubco() {
    return this.currentTeacher.subcoFor(this.currentSubscriptionType)
  }

  get limitedAccess() {
    return isNone(this.model.subscriptionUtilisation)
  }

  get isOverCapacity() {
    return this.model.subscriptionUtilisation?.isOverCapacity
  }

  get subscribedStudentCount() {
    return this.model.subscriptionUtilisation?.studentCount
  }

  get subscriptionCapacitySize() {
    return this.model.subscriptionUtilisation?.capacitySize
  }

  get schoolStudentCount() {
    return this.model.schoolStatistics?.totalStudentCount
  }

  get currentSubscriptionType() {
    return this.model.subscriptionType
  }

  get overCapacityTooltipMessage() {
    if (this.isSubco) {
      return this.intl.t('pageHeader.overCapacityTooltipForSubco')
    } else if (this.subscriptionType.currentSubco) {
      return this.intl.t('pageHeader.overCapacityTooltipForTeacherWithSubco')
    }
    return this.intl.t('pageHeader.overCapacityTooltipForTeacherNoSubco')
  }

  @tracked currentlyLoading = false

  constructor(...args: [Owner]) {
    super(...args)
    this.router.on('routeWillChange', this._routeWillChange)
  }

  willDestroy() {
    this.router.off('routeWillChange', this._routeWillChange)
  }

  _routeWillChange = (transition: Transition) => {
    /* There should only be one valid transition at a time, (new transitions abort previous ones) so if another comes in,
     * there's no need to do anything. followRedirects() on the original transition will follow it.
     * See ember-loading for an alternative approach (resolving a promise for each individual transition)
     * https://github.com/kaliber5/ember-loading/blob/8f979b830ab1fed67692196d33543f9b1c969f39/addon/services/loading.ts#L68-L102
     */
    if (!this.currentlyLoading) {
      const showLoader = (val: boolean) => {
        this.currentlyLoading = val
      }
      showLoader(true)

      /* This will wait for all potential redirects of the transition to finish, before hiding the loader
       * Using finally (rather than 'then'), as this will reject if the final transition or redirect is aborted, in
       * which case we should still hide the loader.
       */
      void transition.followRedirects().finally(() => {
        showLoader(false)
      })
    }
  }
}
